import { Card, DataList, EmptyView, ErrorView, ListControls, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { useNicheLabels } from '../../components/dashboard/useNicheLabels';
import { usePaginatedList } from '../../components/dashboard/usePaginatedList';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { FormatLocalized, FormatTimestamp, WrapLTR } from '@shared/common';
import { BookingClient } from '@shared/frontend';

/**
 * Bookings screen: appointments the assistant booked, one bubble per booking (data layout). Read-only — bookings live on the tenant's connected calendar, so completion is reconciled there.
 *
 * @returns The bookings screen element.
 */
export function Bookings() {
  const { tenantID } = useDashboard();
  const list = usePaginatedList(
    (signal, offset, limit, pattern) => BookingClient.ListBookings(signal, tenantID!, offset, limit, pattern),
    (signal, pattern) => BookingClient.CountBookings(signal, tenantID!, pattern),
    [tenantID]
  );
  const labels = useNicheLabels();

  return (
    <>
      <ListControls
        searchValue={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={Localized.BookingsSearchPlaceholder}
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        pageCount={list.data.length}
        onOffsetChange={list.setOffset}
        onLimitChange={list.setLimit}
        loading={list.loading}
      />
      <Card title={FormatLocalized(Localized.PanelListSuffix, labels.bookings)} titleHint={Localized.BookingsHint}>
        {list.loading ? (
          <LoadingView />
        ) : list.error ? (
          <ErrorView onRetry={list.reload} />
        ) : list.data.length === 0 ? (
          <EmptyView message={Localized.BookingsNone} />
        ) : (
          <div className={Styles.cardStack}>
            {list.data.map((booking) => (
              <div key={booking.id} className={Styles.subPanel}>
                <DataList
                  rows={[
                    { label: Localized.AccountDisplayName, value: booking.customerName },
                    { label: Localized.SharedContactPhone, value: WrapLTR(booking.customerPhoneE164) },
                    { label: Localized.BookingsWhen, value: `${FormatTimestamp(booking.startISO)} – ${FormatTimestamp(booking.endISO)}` },
                    booking.notes ? { label: Localized.BookingsNotes, value: booking.notes } : null,
                    { label: Localized.SharedStatus, value: <StatusBadge status={booking.status} /> }
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
